import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Row,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Input,
} from 'reactstrap';
import Select from 'react-select';
import { Colxx } from '../../../../../components/common/CustomBootstrap';
import WorkOrderModal from './components/WorkOrderModal';
import classnames from 'classnames';
import WorkOrdersKanbanBoard from './components/KanbanBoard/Kanban';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import WorkOrdersList from './components/List/workOrderList';
import { useSelector } from 'react-redux';
import { NotificationManager } from '../../../../../components/common/react-notifications';
import { AxiosAPI, getGroupID } from '../../../../../helpers/AxiosAPI';
import { API_WorkOrders } from '../../../../../constants/APIs';

const STATUS_FILTER_OPTIONS = [
  { label: 'Submitted', value: 'Submitted' },
  { label: 'Acknowledged', value: 'Acknowledged' },
  { label: 'Completed', value: 'Completed' },
  { label: 'Archived', value: 'Archived' },
  { label: 'Cancelled', value: 'Cancelled' },
];

const GuestMessagingWorkOrders = () => {
  const [visible, setVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('board');

  const [workOrders, setWorkOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Board-specific filters
  const [boardStatusFilter, setBoardStatusFilter] = useState([]);
  const [boardPendingStatusFilter, setBoardPendingStatusFilter] = useState([]);
  const [boardSearchKeyword, setBoardSearchKeyword] = useState('');

  // List-specific filters & pagination
  const [listStatusFilter, setListStatusFilter] = useState([]);
  const [listPendingStatusFilter, setListPendingStatusFilter] = useState([]);
  const [listSearchKeyword, setListSearchKeyword] = useState('');
  const [page, setPage] = useState(0);

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  const { propertyId, groupId: storedGroupId } = useSelector(
    ({ settings }) => ({
      propertyId: settings.propertyId,
      groupId: settings.groupId,
    })
  );
  const effectiveGroupId = storedGroupId || getGroupID();

  const styleCss = `.nav-tabs .nav-link.active::before,.nav-tabs .nav-item.show .nav-link::before {
        top:auto !important;
        bottom: 0 !important;
      }
      .nav-tabs .nav-link {
        border: none;
      }
      .nav-tabs .nav-link.active,
      .nav-tabs .nav-link:hover {
        background-color: transparent;
      }`;

  const toggleModel = () => setVisible((prev) => !prev);

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  const toggleFilterPanel = () => {
    setIsFilterPanelOpen((prev) => {
      const next = !prev;
      if (!prev) {
        if (activeTab === 'board') {
          setBoardPendingStatusFilter([...boardStatusFilter]);
        } else {
          setListPendingStatusFilter([...listStatusFilter]);
        }
      }
      return next;
    });
  };

  const handleApplyStatusFilter = () => {
    if (activeTab === 'board') {
      setBoardStatusFilter([...boardPendingStatusFilter]);
    } else {
      setListStatusFilter([...listPendingStatusFilter]);
    }
    setIsFilterPanelOpen(false);
  };

  const handleClearStatusFilter = () => {
    if (activeTab === 'board') {
      setBoardPendingStatusFilter([]);
      setBoardStatusFilter([]);
    } else {
      setListPendingStatusFilter([]);
      setListStatusFilter([]);
    }
    setIsFilterPanelOpen(false);
  };

  const fetchWorkOrders = useCallback(async () => {
    if (!propertyId || !effectiveGroupId) {
      setWorkOrders([]);
      return;
    }

    setIsLoading(true);
    try {
      // Note: Currently fetching all records. If the API supports pagination/search,
      // we should pass those parameters here. For now, doing it locally if needed.
      const response = await AxiosAPI({
        method: 'get',
        url: API_WorkOrders,
        params: {
          groupId: effectiveGroupId,
          propertyId: propertyId,
        },
      });

      const data = Array.isArray(response.data) ? response.data : [];
      setWorkOrders(data);
    } catch (err) {
      NotificationManager.error('Failed to load work orders', 'Error', 3000);
      setWorkOrders([]);
    } finally {
      setIsLoading(false);
    }
  }, [effectiveGroupId, propertyId]);

  useEffect(() => {
    fetchWorkOrders();
  }, [fetchWorkOrders]);

  // Apply board filters locally
  const filteredBoardWorkOrders = useMemo(() => {
    let filtered = workOrders;
    if (boardStatusFilter.length > 0) {
      const statuses = boardStatusFilter.map((s) => s.value.toLowerCase());
      filtered = filtered.filter((wo) =>
        statuses.includes((wo.status || '').toLowerCase())
      );
    }
    if (boardSearchKeyword.trim()) {
      const search = boardSearchKeyword.toLowerCase().trim();
      filtered = filtered.filter(
        (wo) =>
          (wo.workOrderType || '').toLowerCase().includes(search) ||
          (wo.message || '').toLowerCase().includes(search) ||
          (wo.roomTitle || '').toLowerCase().includes(search)
      );
    }
    return filtered;
  }, [workOrders, boardStatusFilter, boardSearchKeyword]);

  // Apply list filters locally (and simple pagination)
  const LIST_PAGE_SIZE = 10;
  const filteredListWorkOrders = useMemo(() => {
    let filtered = workOrders;
    if (listStatusFilter.length > 0) {
      const statuses = listStatusFilter.map((s) => s.value.toLowerCase());
      filtered = filtered.filter((wo) =>
        statuses.includes((wo.status || '').toLowerCase())
      );
    }
    if (listSearchKeyword.trim()) {
      const search = listSearchKeyword.toLowerCase().trim();
      filtered = filtered.filter(
        (wo) =>
          (wo.workOrderType || '').toLowerCase().includes(search) ||
          (wo.message || '').toLowerCase().includes(search) ||
          (wo.roomTitle || '').toLowerCase().includes(search)
      );
    }
    return filtered;
  }, [workOrders, listStatusFilter, listSearchKeyword]);

  const paginatedListWorkOrders = useMemo(() => {
    const start = page * LIST_PAGE_SIZE;
    return filteredListWorkOrders.slice(start, start + LIST_PAGE_SIZE);
  }, [filteredListWorkOrders, page]);

  const handleSearchKeyDown = useCallback((event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      // Enter just updates the filters which is already handled via onChange for local search
    }
  }, []);

  const handleClearSearch = useCallback(() => {
    if (activeTab === 'board') {
      setBoardSearchKeyword('');
    } else {
      setListSearchKeyword('');
      setPage(0);
    }
  }, [activeTab]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', }}>
      <style dangerouslySetInnerHTML={{ __html: styleCss }} />
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h4 className="mb-0"><b> Work Orders</b></h4>
          <p className="mb-0 text-muted">
            Manage your property's work orders effectively.
          </p>
        </div>
      </div>

      <Row>
        <Colxx xxs="12" className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="flex-grow-1">
              <Nav tabs className="card-header-tabs separator-tabs mb-0 ml-0 mr-0">
                <NavItem className="text-center" style={{ width: 'auto' }}>
                  <NavLink
                    className={classnames({
                      active: activeTab === 'board',
                      'nav-link': true,
                    })}
                    onClick={() => toggleTab('board')}
                    style={{ background: 'none', cursor: 'pointer' }}
                  >
                    <span
                      style={{
                        color: activeTab === 'board' ? 'var(--theme-color-1)' : '#8f8f8f',
                        fontWeight: 600,
                      }}
                    >
                      Board
                    </span>
                  </NavLink>
                </NavItem>
                <NavItem className="text-center" style={{ width: 'auto' }}>
                  <NavLink
                    className={classnames({
                      active: activeTab === 'list',
                      'nav-link': true,
                    })}
                    onClick={() => toggleTab('list')}
                    style={{ background: 'none', cursor: 'pointer' }}
                  >
                    <span
                      style={{
                        color: activeTab === 'list' ? 'var(--theme-color-1)' : '#8f8f8f', 
                        fontWeight: 600,
                      }}
                    >
                      List
                    </span>
                  </NavLink>
                </NavItem>
              </Nav>
            </div>
          </div>

          <div className="top-left-button-container mt-3 mb-3 d-flex align-items-center">
            <div className="position-relative mr-2">
              <Button
                color="primary"
                size="xs"
                className="d-flex align-items-center"
                onClick={toggleFilterPanel}
              >
                <FontAwesomeIcon icon={faFilter} className="fa mr-1 mt-1" />
                Filters
                <i
                  className={`simple-icon-arrow-${isFilterPanelOpen ? 'up' : 'down'} ml-2`}
                  style={{ fontSize: 10 }}
                />
              </Button>

              {isFilterPanelOpen && (
                <div
                  className="reservation-filter-panel"
                  style={{
                    position: 'absolute',
                    top: '110%',
                    left: 0,
                    width: 260,
                    zIndex: 12,
                    background: '#fff',
                    borderRadius: 12,
                    boxShadow: '0 10px 30px rgba(15, 34, 58, 0.15)',
                    padding: '16px',
                  }}
                >
                  <div className="mb-3">
                    <label className="text-small font-weight-semibold d-block mb-1">
                      Status
                    </label>
                    <Select
                      isMulti
                      options={STATUS_FILTER_OPTIONS}
                      className="react-select"
                      classNamePrefix="react-select"
                      placeholder="Select Status"
                      value={
                        activeTab === 'board'
                          ? boardPendingStatusFilter
                          : listPendingStatusFilter
                      }
                      onChange={(val) => {
                        if (activeTab === 'board')
                          setBoardPendingStatusFilter(val || []);
                        else setListPendingStatusFilter(val || []);
                      }}
                    />
                  </div>
                  <div className="d-flex justify-content-end mt-4 pt-2 border-top">
                    <Button
                      size="xs"
                      color="secondary"
                      onClick={handleClearStatusFilter}
                    >
                      Clear
                    </Button>
                    <Button
                      size="xs"
                      color="primary"
                      className="ml-2"
                      onClick={handleApplyStatusFilter}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <Button
              color="primary"
              size="xs"
              outline
              className="top-left-button mr-1"
              onClick={toggleModel}
            >
              + Add New Work Order
            </Button>

            <div className="d-inline-flex align-items-center ml-auto">
              <Input
                type="text"
                name="keyword"
                id="search"
                placeholder="Search"
                value={
                  activeTab === 'board' ? boardSearchKeyword : listSearchKeyword
                }
                onChange={(e) => {
                  if (activeTab === 'board')
                    setBoardSearchKeyword(e.target.value);
                  else {
                    setListSearchKeyword(e.target.value);
                    setPage(0);
                  }
                }}
                onKeyDown={handleSearchKeyDown}
                autoComplete="off"
                style={{ width: 200, borderRadius: 50 }}
              />
              <Button
                size="xs"
                color="primary"
                className="ml-2"
                style={{ padding: '6px' }}
              >
                Search
              </Button>
              {(activeTab === 'board'
                ? boardSearchKeyword
                : listSearchKeyword) && (
                  <Button
                    size="xs"
                    color="secondary"
                    className="ml-2"
                    outline
                    onClick={handleClearSearch}
                  >
                    Clear
                  </Button>
                )}
            </div>
          </div>

          <TabContent activeTab={activeTab}>
            <TabPane tabId="board">
              <WorkOrdersKanbanBoard
                data={filteredBoardWorkOrders}
                loading={isLoading}
                onRefresh={fetchWorkOrders}
              />
            </TabPane>
            <TabPane tabId="list">
              <WorkOrdersList
                data={paginatedListWorkOrders}
                loading={isLoading}
                onRefresh={fetchWorkOrders}
                page={page}
                pageSize={LIST_PAGE_SIZE}
                totalPages={Math.ceil(
                  filteredListWorkOrders.length / LIST_PAGE_SIZE
                )}
                totalItems={filteredListWorkOrders.length}
                onPageChange={setPage}
              />
            </TabPane>
          </TabContent>
        </Colxx>
      </Row>

      <WorkOrderModal
        visible={visible}
        toggle={toggleModel}
        onSuccess={fetchWorkOrders}
      />
    </div>
  );
};

export default GuestMessagingWorkOrders;
